import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Map as MapGL,
  Marker,
  Popup,
  NavigationControl,
} from "@vis.gl/react-mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { motion, AnimatePresence } from "framer-motion";
import { Slider } from "./ui/slider";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent } from "./ui/dialog";
import {
  MapPin,
  Star,
  Sparkles,
  SlidersHorizontal,
  X,
  ArrowRight,
  Clock,
  Search,
  User,
} from "lucide-react";
import { previewOne } from "../lib/api";
import mapboxgl from "mapbox-gl";
import favicon from "../images/favicon.png";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

export default function MatchesModern() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const bookingContext = state?.context || {};

  // Extract referral code from navigation state or localStorage
  const [referralCode, setReferralCode] = useState(state?.referralCode || null);

  // Check for referral code in localStorage if not in navigation state
  useEffect(() => {
    if (!referralCode) {
      const stored = localStorage.getItem("bookatherapy_referralCode");
      if (stored) {
        console.log(
          "🎟️ [MatchesModern] Referral code loaded from localStorage:",
          stored,
        );
        setReferralCode(stored);
      }
    } else {
      console.log(
        "🎟️ [MatchesModern] Referral code received from navigation:",
        referralCode,
      );
    }
  }, [referralCode]);

  const [therapists, setTherapists] = useState([]);
  const [clinicsMap, setClinicsMap] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [popupInfo, setPopupInfo] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    gender: "",
    rating: 0,
    distance: 50,
    directBilling: false,
  });
  const [isMobile, setIsMobile] = useState(false);
  const [mapModalTherapist, setMapModalTherapist] = useState(null);

  const mapRef = useRef(null);
  const mapLoadedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const media = window.matchMedia("(max-width: 1023px)");
    const handleChange = (event) => setIsMobile(event.matches);

    handleChange(media);
    if (media.addEventListener) {
      media.addEventListener("change", handleChange);
    } else {
      media.addListener(handleChange);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener("change", handleChange);
      } else {
        media.removeListener(handleChange);
      }
    };
  }, []);

  // Load therapists from router state or fetch from API
  useEffect(() => {
    console.log("🔄 useEffect triggered, state:", state);

    if (state?.results) {
      console.log(
        "📥 Loading from state.results:",
        state.results.length,
        "therapists",
      );
      setTherapists(state.results);
    } else if (state?.grouped) {
      console.log("📦 state.grouped:", state.grouped);
      // Deduplicate therapists across multiple settings
      const therapistMap = new Map();

      Object.entries(state.grouped).forEach(([setting, data]) => {
        console.log(`🔍 Processing setting ${setting}:`, {
          dataType: typeof data,
          isArray: Array.isArray(data),
          hasResults: !!(data && typeof data === "object" && data.results),
          dataKeys: data && typeof data === "object" ? Object.keys(data) : [],
        });
        
        // Handle both cases: data can be an array directly, or an object with results property
        let results = [];
        if (Array.isArray(data)) {
          // Data is already an array (from matchByReviews when no persona)
          results = data;
          console.log(`  ✅ Setting ${setting}: Found ${results.length} therapists (array)`);
        } else if (data?.results && Array.isArray(data.results)) {
          // Data is an object with results property (from previewOne with persona)
          results = data.results;
          console.log(`  ✅ Setting ${setting}: Found ${results.length} therapists (object.results)`);
        } else if (data?.results) {
          // Fallback: if results exists but isn't an array, try to use it
          results = Array.isArray(data.results) ? data.results : [];
          console.log(`  ⚠️ Setting ${setting}: Fallback, found ${results.length} therapists`);
        } else {
          console.log(`  ❌ Setting ${setting}: No therapists found, data:`, data);
        }
        
        results.forEach((therapist, idx) => {
          const id = therapist.therapistId || therapist._id;
          if (!id) {
            console.warn(`  ⚠️ Therapist at index ${idx} has no ID:`, therapist);
            return;
          }
          if (therapistMap.has(id)) {
            // Therapist already exists, add this setting to their available settings
            const existing = therapistMap.get(id);
            if (!existing.availableSettings) {
              existing.availableSettings = [
                existing._originalSetting || setting,
              ];
            }
            if (!existing.availableSettings.includes(setting)) {
              existing.availableSettings.push(setting);
            }
            // Store settings with their specific data (like different slots per setting)
            if (!existing.settingsData) {
              existing.settingsData = {};
            }
            existing.settingsData[setting] = {
              slots: therapist.slots,
              fallbackSlots: therapist.fallbackSlots,
            };
          } else {
            // First time seeing this therapist
            therapistMap.set(id, {
              ...therapist,
              _originalSetting: setting,
              availableSettings: [setting],
              settingsData: {
                [setting]: {
                  slots: therapist.slots,
                  fallbackSlots: therapist.fallbackSlots,
                },
              },
            });
          }
        });
      });

      const all = Array.from(therapistMap.values());
      console.log("📥 Deduplicated therapists:", all);
      console.log("📥 Total count:", all.length);
      setTherapists(all);
    } else {
      // Fallback: fetch therapists if no state provided (direct navigation)
      console.log("🌐 No state found, fetching from API...");
      const fetchTherapists = async () => {
        try {
          const data = await previewOne({
            setting: "Clinic",
            dateISO: "2025-11-15",
            persona: null,
            clientPostalCode: null,
            serviceId: null,
          });
          console.log("📥 API Response:", data);
          console.log("📥 Results array:", data?.results);
          console.log("📥 Results length:", data?.results?.length);

          if (data?.results) {
            console.log("✅ Setting therapists:", data.results);
            setTherapists(data.results);
          } else {
            console.log("❌ No results in API response");
          }
        } catch (error) {
          console.error("❌ Failed to fetch therapists:", error);
        }
      };
      fetchTherapists();
    }
  }, [state]);

  // Extract clinics (already populated by backend)
  useEffect(() => {
    const clinicsById = {};
    therapists.forEach((t) => {
      if (t.clinic?._id) clinicsById[t.clinic._id] = t.clinic;
    });
    setClinicsMap(clinicsById);
  }, [therapists]);

  // Filters
  const filteredTherapists = useMemo(() => {
    console.log("🔍 Filtering therapists:", {
      totalTherapists: therapists.length,
      filters,
    });

    const query = filters.search?.toLowerCase().trim() || "";

    const filtered = therapists.filter((t) => {
      if (query) {
        const haystack = [
          t.fullName,
          t.bio,
          ...(t.specialties || []),
          ...(t.modalities || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(query)) {
          console.log(`❌ ${t.fullName} filtered out by search query`);
          return false;
        }
      }

      // Gender filter
      if (filters.gender && t.gender !== filters.gender) {
        console.log(
          `❌ ${t.fullName} filtered out by gender: ${t.gender} !== ${filters.gender}`,
        );
        return false;
      }

      // Rating filter
      if (filters.rating && (t.rating ?? 0) < filters.rating) {
        console.log(
          `❌ ${t.fullName} filtered out by rating: ${t.rating ?? 0} < ${
            filters.rating
          }`,
        );
        return false;
      }

      // Distance filter - only filter if distance has a value
      if (
        filters.distance &&
        t.distanceKm != null &&
        t.distanceKm > filters.distance
      ) {
        console.log(
          `❌ ${t.fullName} filtered out by distance: ${t.distanceKm} > ${filters.distance}`,
        );
        return false;
      }

      // Direct billing filter
      if (filters.directBilling && !t.directBilling) {
        console.log(`❌ ${t.fullName} filtered out by directBilling`);
        return false;
      }

      console.log(`✅ ${t.fullName} passed all filters`);
      return true;
    });

    console.log("✅ Filtered therapists count:", filtered.length);
    return filtered;
  }, [therapists, filters]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.search) count += 1;
    if (filters.gender) count += 1;
    if (filters.rating) count += 1;
    if (filters.distance && filters.distance < 50) count += 1;
    if (filters.directBilling) count += 1;
    return count;
  }, [filters]);

  const formattedBookingDate = useMemo(() => {
    if (!bookingContext?.dateISO) return null;
    try {
      return new Date(bookingContext.dateISO).toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    } catch (err) {
      console.error("Failed to format booking date", err);
      return null;
    }
  }, [bookingContext?.dateISO]);

  useEffect(() => {
    if (!filteredTherapists.length) {
      setSelectedTherapist(null);
      setPopupInfo(null);
      return;
    }

    setSelectedTherapist((prev) => {
      if (
        prev &&
        filteredTherapists.some((t) => t.therapistId === prev.therapistId)
      ) {
        return prev;
      }
      return filteredTherapists[0];
    });
  }, [filteredTherapists]);

  const therapistCoordinates = useMemo(
    () =>
      filteredTherapists
        .map((t) => t.clinic?.location?.coordinates)
        .filter(
          (coords) =>
            Array.isArray(coords) &&
            coords.length === 2 &&
            !coords.some((value) => Number.isNaN(Number(value))),
        ),
    [filteredTherapists],
  );

  // Default center (Edmonton) if nothing to show
  const mapCenter = filteredTherapists[0]?.clinic?.location?.coordinates
    ? {
        longitude: filteredTherapists[0].clinic.location.coordinates[0],
        latitude: filteredTherapists[0].clinic.location.coordinates[1],
      }
    : { longitude: -113.4938, latitude: 53.5461 };

  // Helper: flyTo only after the map is fully ready
  const safeFlyTo = (opts) => {
    const mapObj = mapRef.current?.getMap?.();
    if (!mapObj) return;

    const doFly = () => {
      try {
        mapRef.current.flyTo(opts);
      } catch {}
    };

    if (mapLoadedRef.current && (mapObj.isStyleLoaded?.() ?? true)) {
      doFly();
    } else {
      const handleLoad = () => {
        mapLoadedRef.current = true;
        doFly();
        mapObj.off("load", handleLoad);
      };
      mapObj.on("load", handleLoad);
    }
  };

  useEffect(() => {
    if (!mapRef.current || therapistCoordinates.length === 0) return;

    const mapInstance = mapRef.current.getMap?.();
    if (!mapInstance) return;

    if (therapistCoordinates.length === 1) {
      const [lng, lat] = therapistCoordinates[0];
      safeFlyTo({
        center: [lng, lat],
        zoom: 13,
        duration: 600,
        essential: true,
      });
      return;
    }

    const bounds = therapistCoordinates.reduce(
      (acc, coord) => acc.extend(coord),
      new mapboxgl.LngLatBounds(
        therapistCoordinates[0],
        therapistCoordinates[0],
      ),
    );

    const fitToBounds = () => {
      try {
        mapRef.current.fitBounds(bounds, {
          padding: 80,
          duration: 700,
        });
      } catch {}
    };

    if (mapLoadedRef.current && (mapInstance.isStyleLoaded?.() ?? true)) {
      fitToBounds();
    } else {
      const handleLoad = () => {
        mapLoadedRef.current = true;
        fitToBounds();
        mapInstance.off("load", handleLoad);
      };
      mapInstance.on("load", handleLoad);
    }
  }, [therapistCoordinates]);

  // Zoom to selected therapist
  useEffect(() => {
    const coords = selectedTherapist?.clinic?.location?.coordinates;
    if (!coords) return;

    const [lng, lat] = coords;
    const clinic = selectedTherapist?.clinic?._id
      ? clinicsMap[selectedTherapist.clinic._id]
      : selectedTherapist?.clinic;

    setPopupInfo({
      therapist: selectedTherapist,
      clinic,
      lng,
      lat,
    });

    if (filteredTherapists.length > 1) {
      return;
    }

    safeFlyTo({
      center: [lng, lat],
      zoom: 15.5,
      duration: 700,
      essential: true,
      offset: [0, 0],
    });
  }, [selectedTherapist, clinicsMap, filteredTherapists.length]);

  // When a popup opens (marker clicked), also center/zoom it
  useEffect(() => {
    if (!popupInfo) return;
    if (therapistCoordinates.length > 1) return;

    safeFlyTo({
      center: [popupInfo.lng, popupInfo.lat],
      zoom: 16.5,
      duration: 600,
      essential: true,
      offset: [0, 0],
    });
  }, [popupInfo, therapistCoordinates.length]);

  const goToConfirm = (therapist) => {
    navigate("/book/confirm", {
      state: {
        selection: {
          therapistId: therapist.therapistId,
          fullName: therapist.fullName,
          photoUrl: therapist.photoUrl,
          availableSettings: therapist.availableSettings || [
            state?.context?.setting,
          ],
          settingsData: therapist.settingsData,
        },
        context: state?.context,
        referralCode, // Pass referral code through navigation
      },
    });
  };

  const mapModalClinic = mapModalTherapist?.clinic?._id
    ? clinicsMap[mapModalTherapist.clinic._id] || mapModalTherapist.clinic
    : mapModalTherapist?.clinic;
  const mapModalCoordinates =
    mapModalClinic?.location?.coordinates ||
    mapModalTherapist?.clinic?.location?.coordinates ||
    null;
  const mapModalViewState = mapModalCoordinates
    ? {
        longitude: mapModalCoordinates[0],
        latitude: mapModalCoordinates[1],
        zoom: 14,
      }
    : { ...mapCenter, zoom: 12 };

  return (
    <>
      <div className="h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 shadow-sm z-30">
          <div
            className={`px-4 sm:px-6 lg:px-8 ${
              isMobile ? "py-3 space-y-3" : "py-4 space-y-4"
            }`}>
            <div
              className={`flex flex-wrap items-center justify-between ${
                isMobile ? "gap-2" : "gap-3"
              }`}>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition">
                  <ArrowRight className="w-5 h-5 rotate-180" />
                </button>
                <div>
                  <h1
                    className={`font-bold text-gray-900 ${
                      isMobile ? "text-lg" : "text-2xl"
                    }`}>
                    Find Your Therapist
                  </h1>
                  <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                    <Sparkles className="w-4 h-4 text-emerald-500" />
                    {filteredTherapists.length} therapists available
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setShowFilters(true)}
                  className="hidden md:inline-flex bg-emerald-600 hover:bg-emerald-700 text-white gap-2">
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                  {activeFiltersCount > 0 && (
                    <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">
                      {activeFiltersCount}
                    </span>
                  )}
                </Button>
                <Button
                  onClick={() => setShowFilters(true)}
                  className="md:hidden inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                </Button>
              </div>
            </div>
            <div
              className={`flex ${
                isMobile
                  ? "flex-col gap-2"
                  : "flex-col gap-3 md:flex-row md:items-center md:justify-between"
              }`}>
              <div className="relative w-full md:max-w-md">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      search: e.target.value,
                    }))
                  }
                  type="search"
                  placeholder="Search by therapist name, specialty, or modality"
                  className={`w-full rounded-lg border border-gray-200 bg-white pl-10 pr-3 text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                    isMobile ? "py-2.5 h-11" : "py-2.5"
                  }`}
                />
              </div>
              <div
                className={`text-xs text-gray-500 ${
                  isMobile
                    ? "flex items-center gap-2 overflow-x-auto pb-1 -mx-4 px-4"
                    : "flex flex-wrap items-center gap-2"
                }`}>
                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">
                  {filteredTherapists.length} matches shown
                </span>
                {filters.distance < 50 && (
                  <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                    Within {filters.distance} km
                  </span>
                )}
                {filters.rating > 0 && (
                  <span className="px-3 py-1 rounded-full bg-yellow-50 text-yellow-700 font-medium">
                    {filters.rating}★+
                  </span>
                )}
                {filters.directBilling && (
                  <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 font-medium">
                    Direct billing
                  </span>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main */}
        <div className="flex-1 flex flex-col lg:flex-row lg:gap-6 overflow-hidden relative bg-white">
          <div
            className={`w-full lg:basis-1/2 lg:max-w-none lg:pr-4 overflow-y-auto bg-gray-50 space-y-4 ${
              isMobile ? "pb-20" : ""
            }`}>
            <div className="sticky top-0 z-10 bg-gray-50 pb-4 p-4 sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-emerald-600 font-semibold">
                    Personalized matches
                  </p>
                  <h2 className="text-xl font-semibold text-gray-900 mt-1">
                    {filteredTherapists.length} therapists for you
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {formattedBookingDate && (
                      <span>{formattedBookingDate}</span>
                    )}
                    {bookingContext?.city && (
                      <span>
                        {formattedBookingDate ? " • " : ""}
                        {bookingContext.city}
                      </span>
                    )}
                    {bookingContext?.service?.name && (
                      <span>
                        {formattedBookingDate || bookingContext?.city
                          ? " • "
                          : ""}
                        {bookingContext.service.name}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-4">
              <AnimatePresence>
                {filteredTherapists.map((therapist, idx) => (
                  <TherapistCard
                    key={therapist.therapistId}
                    therapist={therapist}
                    clinic={clinicsMap[therapist.clinic?._id]}
                    onSelect={() => {
                      setSelectedTherapist(therapist);
                    }}
                    onBook={() => goToConfirm(therapist)}
                    onViewMap={() => {
                      setSelectedTherapist(therapist);
                      setMapModalTherapist(therapist);
                    }}
                    bookingDateISO={bookingContext?.dateISO}
                    isSelected={
                      selectedTherapist?.therapistId === therapist.therapistId
                    }
                    isMobile={isMobile}
                    delay={idx * 0.05}
                  />
                ))}
              </AnimatePresence>
            </div>

            {filteredTherapists.length === 0 && (
              <div className="text-center py-20">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No therapists found
                </h3>
                <p className="text-gray-500">Try adjusting your filters</p>
              </div>
            )}
          </div>

          <div className="hidden lg:block lg:basis-1/2 lg:flex-shrink-0 relative">
            <MapGL
              ref={mapRef}
              onLoad={() => {
                mapLoadedRef.current = true;
              }}
              initialViewState={{
                ...mapCenter,
                zoom: 11,
              }}
              style={{ width: "100%", height: "100%" }}
              mapStyle="mapbox://styles/mapbox/streets-v12"
              mapboxAccessToken={MAPBOX_TOKEN}>
              <NavigationControl position="top-right" />

              {filteredTherapists.map((therapist) => {
                if (!therapist.clinic?.location?.coordinates) return null;
                const [lng, lat] = therapist.clinic.location.coordinates;
                const clinic = clinicsMap[therapist.clinic._id];
                const isActive =
                  selectedTherapist?.therapistId === therapist.therapistId;

                return (
                  <Marker
                    key={therapist.therapistId}
                    longitude={lng}
                    latitude={lat}
                    anchor="bottom"
                    color="#252F4F"
                    onClick={(e) => {
                      e.originalEvent.stopPropagation();
                      setSelectedTherapist(therapist);
                      setPopupInfo({ therapist, clinic, lng, lat });
                    }}>
                    <div
                      className={`cursor-pointer transition-transform ${
                        isActive ? "scale-110" : "hover:scale-110"
                      }`}>
                      <div
                        className={`w-10 h-10 rounded-full shadow-lg border-4 flex items-center justify-center ${
                          isActive
                            ? "bg-emerald-600 border-emerald-200"
                            : "bg-gradient-to-br from-emerald-500 to-emerald-600 border-white"
                        }`}>
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </Marker>
                );
              })}

              {popupInfo && (
                <Popup
                  longitude={popupInfo.lng}
                  latitude={popupInfo.lat}
                  anchor="bottom"
                  onClose={() => setPopupInfo(null)}
                  closeOnClick={false}
                  className="custom-popup">
                  <MapPopupContent
                    therapist={popupInfo.therapist}
                    clinic={popupInfo.clinic}
                    onClick={() => goToConfirm(popupInfo.therapist)}
                  />
                </Popup>
              )}
            </MapGL>
          </div>

          <AnimatePresence>
            {showFilters && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-black/50 z-40"
                  onClick={() => setShowFilters(false)}
                />

                <motion.div
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{ type: "spring", damping: 30, stiffness: 300 }}
                  className="absolute right-0 top-0 bottom-0 w-full max-w-sm sm:max-w-md md:max-w-lg bg-white shadow-2xl z-50 overflow-y-auto">
                  <FilterPanel
                    filters={filters}
                    setFilters={setFilters}
                    onClose={() => setShowFilters(false)}
                  />
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>

      <Dialog
        open={!!mapModalTherapist}
        onOpenChange={(open) => {
          if (!open) setMapModalTherapist(null);
        }}>
        <DialogContent className="max-w-full w-full h-[calc(100vh-2rem)] sm:h-[90vh] lg:h-[85vh] p-0 overflow-hidden border-none bg-transparent shadow-none">
          {mapModalTherapist && (
            <div className="flex h-full w-full flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl md:rounded-2xl">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">
                    Therapist location
                  </p>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {mapModalTherapist.fullName}
                  </h3>
                  {mapModalTherapist.matchReason && (
                    <p className="text-xs text-emerald-600 mt-1">
                      {mapModalTherapist.matchReason}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setMapModalTherapist(null)}>
                  Close
                </Button>
              </div>
              <div className="relative flex-1">
                <MapGL
                  key={mapModalTherapist.therapistId}
                  initialViewState={mapModalViewState}
                  mapStyle="mapbox://styles/mapbox/streets-v12"
                  mapboxAccessToken={MAPBOX_TOKEN}
                  style={{ width: "100%", height: "100%" }}>
                  <NavigationControl position="top-right" />
                  {mapModalCoordinates && (
                    <Marker
                      longitude={mapModalCoordinates[0]}
                      latitude={mapModalCoordinates[1]}
                      anchor="bottom">
                      <div className="flex flex-col items-center gap-1">
                        <div className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-800 shadow">
                          {mapModalTherapist.fullName.split(" ")[0]}
                        </div>
                        <div className="w-11 h-11 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full shadow-lg border-4 border-white flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    </Marker>
                  )}
                </MapGL>

                <div className="absolute inset-x-0 bottom-0 bg-white/95 backdrop-blur pt-4 pb-5 px-4 shadow-[0_-16px_40px_-20px_rgba(15,23,42,0.45)]">
                  <div className="flex flex-col gap-3">
                    <div>
                      <h4 className="text-base font-semibold text-gray-900">
                        {mapModalTherapist.fullName}
                      </h4>
                      {mapModalClinic?.name && (
                        <p className="text-sm text-gray-600">
                          {mapModalClinic.name}
                        </p>
                      )}
                      {mapModalClinic?.address && (
                        <p className="text-xs text-gray-500">
                          {mapModalClinic.address}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-sm"
                        onClick={() => {
                          if (mapModalTherapist) {
                            const therapist = mapModalTherapist;
                            setMapModalTherapist(null);
                            goToConfirm(therapist);
                          }
                        }}>
                        Book now
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 text-sm"
                        onClick={() => setMapModalTherapist(null)}>
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function TherapistCard({
  therapist,
  clinic,
  onSelect,
  onBook,
  onViewMap,
  bookingDateISO,
  isSelected,
  delay,
  isMobile = false,
}) {
  const defaultImage = favicon;
  const slots = useMemo(() => {
    if (!Array.isArray(therapist.slots)) return [];
    if (!bookingDateISO) return therapist.slots.slice(0, 4);

    try {
      const todayISO = new Date().toISOString().slice(0, 10);
      const isToday = todayISO === bookingDateISO;

      if (!isToday) return therapist.slots.slice(0, 4);

      const now = new Date();
      const filtered = therapist.slots.filter((timeLabel) => {
        const [hoursStr, minutesStr] = String(timeLabel).split(":");
        const hours = Number(hoursStr);
        const minutes = minutesStr ? Number(minutesStr) : 0;
        if (Number.isNaN(hours) || Number.isNaN(minutes)) return true;
        const slotDate = new Date(now);
        slotDate.setHours(hours, minutes, 0, 0);
        return slotDate.getTime() > now.getTime();
      });

      if (!filtered.length && Array.isArray(therapist.fallbackSlots)) {
        return therapist.fallbackSlots.slice(0, 4);
      }

      return filtered.slice(0, 4);
    } catch (err) {
      console.error("Failed to filter slots", err);
      return therapist.slots.slice(0, 4);
    }
  }, [therapist.slots, therapist.fallbackSlots, bookingDateISO]);
  const bioOneLiner = String(
    therapist.bioOneLiner || therapist.bioSummary || "",
  ).trim();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, delay }}
      onMouseEnter={onSelect}
      onFocus={onSelect}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect?.();
        }
      }}
      tabIndex={0}
      className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer group border-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
        isSelected ? "border-emerald-500 shadow-lg" : "border-transparent"
      }`}>
      <div
        className={`flex flex-col ${
          isMobile ? "" : "lg:flex-col lg:gap-0 xl:flex-row"
        }`}>
        {/* Photo */}
        <div
          className={`relative bg-gray-100 overflow-hidden ${
            isMobile
              ? "w-full h-44"
              : "lg:w-full lg:h-56 xl:w-48 xl:h-full xl:min-h-[192px] xl:flex-shrink-0"
          } ${
            isMobile
              ? "rounded-t-2xl"
              : "lg:rounded-t-2xl lg:rounded-b-none xl:rounded-none xl:rounded-l-2xl"
          }`}>
          <img
            src={therapist.photoUrl || defaultImage}
            alt={therapist.fullName}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            style={!therapist.photoUrl ? { imageRendering: "crisp-edges" } : {}}
          />
          {therapist.score && (
            <Badge className="absolute top-3 right-3 bg-emerald-500 text-white border-0 px-2 py-1 text-xs shadow-md">
              {therapist.score}% match
            </Badge>
          )}
        </div>

        {/* Info */}
        <div
          className={`flex-1 min-w-0 flex flex-col ${
            isMobile ? "p-4 gap-4" : "lg:p-6 lg:gap-5 xl:p-6 xl:gap-5"
          }`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h3 className="font-bold text-lg text-gray-900 truncate">
              {therapist.fullName}
            </h3>
            {therapist.matchReason && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                <Sparkles className="w-3 h-3" />
                {therapist.matchReason}
              </span>
            )}
          </div>

          {/* Rating */}
          <div
            className={`flex items-center gap-2 mt-2 ${
              isMobile ? "text-[13px]" : ""
            }`}>
            <div className="flex items-center gap-1 bg-yellow-50 px-2 py-0.5 rounded-full">
              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
              <span className="text-sm font-semibold text-gray-900">
                {therapist.rating || 4.9}
              </span>
            </div>
            <span className="text-xs text-gray-500">
              ({therapist.reviewCount || 0} reviews)
            </span>
            {therapist.experienceYears > 0 && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Clock className="w-3 h-3" />
                {therapist.experienceYears}y experience
              </span>
            )}
            {therapist.distanceKm && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <MapPin className="w-3 h-3" />
                {therapist.distanceKm.toFixed(1)} km away
              </span>
            )}
          </div>

          {/* Clinic */}
          {clinic && (
            <div className="flex items-center gap-1.5 text-sm text-gray-600 mt-3">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-emerald-500" />
              <span className="truncate">{clinic.name}</span>
            </div>
          )}

          {/* Available Settings */}
          {therapist.availableSettings &&
            therapist.availableSettings.length > 1 && (
              <div className="mt-3">
                <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">
                  Available in
                </p>
                <div className="flex flex-wrap gap-2">
                  {therapist.availableSettings.map((setting) => {
                    const settingIcons = {
                      Home: "🏠",
                      Mobile: "🚗",
                      Clinic: "🏥",
                    };
                    return (
                      <span
                        key={setting}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        <span>{settingIcons[setting] || "📍"}</span>
                        {setting}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

          {/* One-line bio */}
          {bioOneLiner && (
            <div className="mt-3 text-sm text-gray-600 italic">
              {bioOneLiner}
            </div>
          )}

          {/* Slots */}
          {slots.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">
                Next available
              </p>
              <div
                className={`flex gap-2 ${
                  isMobile ? "overflow-x-auto pb-1 -mx-1 pr-1" : "flex-wrap"
                }`}>
                {slots.map((slot) => (
                  <span
                    key={slot}
                    className={`px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium ${
                      isMobile ? "flex-shrink-0" : ""
                    }`}>
                    {slot}
                  </span>
                ))}
                {therapist.slots?.length > slots.length && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                    +{therapist.slots.length - slots.length} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-gray-100 px-4 py-4 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-gray-600 flex items-center gap-2">
            {therapist.priceRange?.length === 2 && (
              <span className="font-semibold text-gray-800">
                ${therapist.priceRange[0]} - ${therapist.priceRange[1]} /
                session
              </span>
            )}
            {therapist.basePrice && !therapist.priceRange && (
              <span className="font-semibold text-gray-800">
                ${therapist.basePrice} / session
              </span>
            )}
          </div>
          <div className="flex gap-2 w-full sm:w-auto sm:justify-end">
            {therapist.slug && (
              <Button
                variant="outline"
                className={`text-sm ${isMobile ? "flex-1 h-11" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(
                    `/massage-therapists/${therapist.slug}`,
                    "_blank",
                  );
                }}>
                <User className="w-4 h-4 mr-2" />
                View Profile
              </Button>
            )}
            {isMobile && (
              <Button
                variant="outline"
                className="flex-1 text-sm h-11"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewMap?.();
                }}>
                View map
              </Button>
            )}
            <Button
              className={`text-sm bg-emerald-600 hover:bg-emerald-700 ${
                isMobile ? "flex-1 h-11" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onBook?.();
              }}>
              Book now
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function MapPopupContent({ therapist, clinic, onClick }) {
  const defaultImage = favicon;
  return (
    <div className="p-3 min-w-[220px]">
      <div className="flex items-center gap-3 mb-3">
        <img
          src={therapist.photoUrl || defaultImage}
          alt={therapist.fullName}
          className="w-12 h-12 rounded-full object-cover border-2 border-emerald-100"
          style={!therapist.photoUrl ? { imageRendering: "crisp-edges" } : {}}
        />
        <h3 className="font-bold text-base text-gray-900 flex-1">
          {therapist.fullName}
        </h3>
      </div>
      {clinic && (
        <div>
          <p className="text-sm text-gray-600 mb-1">{clinic.name}</p>
          {clinic.address && (
            <p className="text-xs text-gray-500 mb-2">{clinic.address}</p>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center gap-1 bg-yellow-50 px-2 py-0.5 rounded-full">
          <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
          <span className="text-sm font-semibold">
            {therapist.rating || 4.9}
          </span>
        </div>
        <span className="text-xs text-gray-500">
          ({therapist.reviewCount || 127} reviews)
        </span>
        {therapist.distanceKm && (
          <>
            <span className="text-gray-300">•</span>
            <span className="text-sm text-gray-600">
              {therapist.distanceKm.toFixed(1)} km
            </span>
          </>
        )}
      </div>
      <Button
        onClick={onClick}
        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">
        Book appointment
      </Button>
    </div>
  );
}

function FilterPanel({ filters, setFilters, onClose }) {
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-5 border-b bg-gradient-to-r from-emerald-50 to-teal-50">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-900">Filters</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/50 rounded-lg transition">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-600">Refine your search</p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
        {/* Gender */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-4 text-base">
            Gender Preference
          </h3>
          <div className="grid grid-cols-3 gap-3">
            {["", "male", "female"].map((gender) => (
              <button
                key={gender}
                onClick={() => setFilters({ ...filters, gender })}
                className={`px-4 py-3 rounded-lg font-medium text-sm transition-all ${
                  filters.gender === gender
                    ? "bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}>
                {gender === ""
                  ? "Any"
                  : gender.charAt(0).toUpperCase() + gender.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Distance */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 text-base">Distance</h3>
            <span className="text-lg font-bold text-emerald-600">
              {filters.distance} km
            </span>
          </div>
          <Slider
            value={[filters.distance]}
            onValueChange={(value) =>
              setFilters({ ...filters, distance: value[0] })
            }
            min={5}
            max={50}
            step={5}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>5 km</span>
            <span>50 km</span>
          </div>
        </div>

        {/* Rating */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-4 text-base">
            Minimum Rating
          </h3>
          <div className="grid grid-cols-4 gap-3">
            {[0, 3, 4, 4.5].map((rating) => (
              <button
                key={rating}
                onClick={() => setFilters({ ...filters, rating })}
                className={`px-4 py-3 rounded-lg font-medium text-sm transition-all ${
                  filters.rating === rating
                    ? "bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}>
                {rating === 0 ? "Any" : `${rating}★+`}
              </button>
            ))}
          </div>
        </div>

        {/* Amenities */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-4 text-base">
            Amenities
          </h3>
          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
            <span className="font-medium text-gray-700 text-sm">
              Direct billing available
            </span>
            <Switch
              checked={filters.directBilling}
              onCheckedChange={(checked) =>
                setFilters({ ...filters, directBilling: checked })
              }
            />
          </label>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t bg-gray-50">
        <div className="flex gap-3">
          <button
            onClick={() =>
              setFilters({
                search: "",
                gender: "",
                rating: 0,
                distance: 50,
                directBilling: false,
              })
            }
            className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-gray-700 transition text-sm">
            Reset All
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg hover:shadow-lg font-semibold transition text-sm">
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}
